import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { SectionEyebrow } from "../components/Ornaments";
import { SEO } from "../components/SEO";
import { usePageContent, resolveMediaUrl } from "../components/PageContent";
import { usePageBlocks, PageBlocksRenderer } from "../components/PageBlocks";

export default function Stories() {
  const [stories, setStories] = useState([]);
  const c = usePageContent("stories", {
    eyebrow: "The Archive",
    title: "Stories",
    body: "Field reports, open letters, essays. Filed from gas stations, motel rooms, and the bed of a 1994 Chevy.",
  });
  const blocks = usePageBlocks("stories");

  useEffect(() => {
    api.get("/stories").then((r) => setStories(r.data)).catch(() => {});
  }, []);

  const bg = resolveMediaUrl(c.image);

  return (
    <div className="bg-bone min-h-screen" data-testid="stories-page">
      <SEO
        title={c.title || "Stories"}
        description={c.body}
        image={bg}
        path="/stories"
      />
      <section
        className="relative bg-asphalt text-bone py-16 overflow-hidden"
        style={
          c.show_media && bg
            ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
        data-testid="stories-hero"
      >
        {c.show_media && bg && c.overlay && (
          <div className="absolute inset-0 bg-asphalt/80 mix-blend-multiply pointer-events-none" />
        )}
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          {c.eyebrow && <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">✦ {c.eyebrow}</div>}
          <h1 className="font-display text-6xl sm:text-7xl uppercase leading-none tracking-tighter whitespace-pre-line">{c.title}</h1>
          {c.subtitle && (
            <p className="font-western text-2xl text-bone/80 max-w-2xl mt-4">{c.subtitle}</p>
          )}
          {c.body && (
            <p className="font-western text-2xl text-bone/80 max-w-2xl mt-4 whitespace-pre-line">{c.body}</p>
          )}
        </div>
      </section>
      <div className="tape-divider" />
      <PageBlocksRenderer blocks={blocks} testid="stories-blocks" />
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((s, i) => (
            <Link
              to={`/stories/${s.slug}`}
              key={s.id}
              data-testid={`stories-list-card-${i}`}
              className="block border-2 border-asphalt bg-card-light shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
            >
              <div className="aspect-[4/3] overflow-hidden border-b-2 border-asphalt bg-asphalt">
                {s.cover_image && (
                  <img src={resolveMediaUrl(s.cover_image)} alt={s.title} className="w-full h-full object-cover ink-stamp" />
                )}
              </div>
              <div className="p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-2">
                  ✦ Story №. {String(i + 1).padStart(3, "0")}
                </div>
                <h3 className="font-display text-2xl uppercase tracking-tight leading-tight mb-2">{s.title}</h3>
                <p className="font-western text-lg text-ink mb-3">{s.subtitle}</p>
                <p className="font-body text-sm text-ink line-clamp-3">{s.excerpt}</p>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] mt-4 text-ink">
                  By {s.author || "The Crew"} · {(s.tags || []).join(" · ")}
                </div>
              </div>
            </Link>
          ))}
        </div>
        {stories.length === 0 && (
          <SectionEyebrow>Nothing here yet, partner.</SectionEyebrow>
        )}
      </section>
    </div>
  );
}
